// GENRL Financial Layer — shared types and money helpers.
// All money is stored as integer micros: 1_000_000 micros = 1 GENRL credit = $1.00
// The frontend NEVER does floating-point math on final values.

export type RouteType = "INTERNAL" | "PEER" | "GENRL_NATIVE" | "HYBRID";
export type ServiceType = "COMPUTE" | "INFERENCE" | "STORAGE" | "TRANSFER";
export type BillingUnit =
  | "GPU_HOUR"
  | "VM_HOUR"
  | "TOKEN_1K"
  | "REQUEST"
  | "GPU_SECOND"
  | "GB_HOUR"
  | "VOLUME_HOUR"
  | "GB_TRANSFERRED";
export type BroadcastMode = "INTERNAL_ONLY" | "BROADCAST_ONLY" | "BOTH";
export type PricingMode = "PASSTHROUGH" | "PREMIUM_OR_DISCOUNT" | "FIXED" | "FLOOR_ADJUSTED";
export type SettlementStatus =
  | "PENDING"
  | "METERED"
  | "RECONCILED"
  | "FINALIZED"
  | "PAID"
  | "DISPUTED"
  | "FAILED";

export const MICRO = 1_000_000;

/** Format integer micros as `X.XX credits` (or other suffix). Display only. */
export function formatMicros(
  micros: number | string,
  opts: { suffix?: "credits" | "USD" | "GENRL" | ""; sign?: boolean } = {},
): string {
  const { suffix = "credits", sign = false } = opts;
  const n = typeof micros === "string" ? Number(micros) : micros;
  const value = n / MICRO;
  const abs = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const prefix = sign && value > 0 ? "+" : value < 0 ? "−" : "";
  const unit = suffix ? ` ${suffix}` : "";
  return `${prefix}${abs}${unit}`;
}

export function bpsToPercent(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/* ----------------------- Domain types ----------------------- */

export interface CreditAccount {
  available_credit_micros: string;
  reserved_credit_micros: string;
  spent_credit_micros: string;
  earned_credit_micros: string;
  pending_payout_credit_micros: string;
  peer_spend_credit_micros: string;
  genrl_fees_paid_credit_micros: string;
}

export interface ProviderPricingPolicy {
  source_id: string;
  broadcast_mode: BroadcastMode;
  pricing_mode: PricingMode;
  provider_adjustment_bps: number;
  fixed_unit_price_micros?: string;
  min_unit_price_micros?: string;
  max_unit_price_micros?: string;
  reserve_balance_threshold_micros?: string;
  broadcast_stock_limit?: number;
  allowed_regions?: string[];
  allowed_hardware?: string[];
}

export interface FinancialBid {
  bid_id: string;
  provider_source_id: string;
  provider_owner: string;
  spoke_id: string;
  provider_name: string;
  region: string;
  hardware_type: string;
  gpu_count?: number;
  stock: number;
  service_type: ServiceType;
  billing_unit: BillingUnit;
  raw_unit_cost_micros: string;
  provider_unit_price_micros: string;
  genrl_fee_bps: number;
  genrl_unit_fee_micros: string;
  buyer_unit_price_micros: string;
  route_type: RouteType;
  valid_until: string;
  quote_hash: string;
  reliability: number; // 0..1
}

export interface QuoteResponse {
  quote_id: string;
  bid_id: string;
  route_type: RouteType;
  billing_unit: BillingUnit;
  estimated_quantity: string;
  provider_unit_price_micros: string;
  genrl_fee_bps: number;
  genrl_unit_fee_micros: string;
  buyer_unit_price_micros: string;
  estimated_debit_micros: string;
  reserve_amount_micros: string;
  valid_until: string; // ISO
  quote_hash: string;
}

export interface SettlementRow {
  settlement_id: string;
  reference: string; // job id or volume id
  route_type: RouteType;
  provider: string;
  reserved_amount_micros: string;
  final_buyer_debit_micros: string;
  provider_earnings_micros: string;
  genrl_revenue_micros: string;
  refund_amount_micros: string;
  status: SettlementStatus;
  updated: string;
}

export interface LedgerRow {
  id: string;
  time: string;
  type:
    | "PURCHASE"
    | "RESERVE"
    | "DEBIT"
    | "REFUND"
    | "EARNING"
    | "PAYOUT"
    | "FEE";
  amount_micros: string; // signed
  reference: string;
  route_type?: RouteType;
  status: "POSTED" | "PENDING" | "DISPUTED";
}

export interface PriceIndexRow {
  provider: string;
  route_type: RouteType;
  region: string;
  hardware: string;
  stock: number;
  raw_unit_price_micros: string;
  provider_unit_price_micros: string;
  genrl_unit_fee_micros: string;
  buyer_unit_price_micros: string;
  reliability: number;
  updated: string;
}

export interface ArbitrageOpportunity {
  id: string;
  hardware: string;
  sell_provider: string;
  buy_provider: string;
  sell_unit_price_micros: string;
  buy_unit_price_micros: string;
  spread_micros: string;
  spread_pct: number;
  estimated_hourly_profit_micros: string;
  risk_score: number; // 0..1
  suggestion: "BROADCAST" | "KEEP_INTERNAL" | "REVIEW";
}

export interface FeePolicy {
  id: string;
  route: RouteType;
  service: ServiceType;
  provider: string; // or "*"
  region: string; // or "*"
  hardware: string; // or "*"
  user_tier: string; // or "*"
  fee_bps: number;
  min_fee_micros?: string;
  max_fee_micros?: string;
  effective_from: string;
  effective_to?: string;
  status: "ACTIVE" | "SCHEDULED" | "EXPIRED";
}

/** Build a FinancialBid from a live offer row plus optional precomputed financial fields. */
export function buildFinancialBid(
  bid: {
    source: string;
    gpu: string;
    location: string;
    price: number;
    capacity: number;
    reference: boolean;
    routeType?: RouteType;
    sourceSlug?: string;
    providerSlug?: string;
    routeID?: string;
    optionID?: string;
    spokeID?: string;
    providerUnitMicros?: number;
    genrlFeeMicros?: number;
    buyerUnitMicros?: number;
  },
  i: number,
): FinancialBid {
  const route: RouteType = bid.routeType || "PEER";
  const raw = Math.max(0, Math.round(bid.price * MICRO));
  const prov = Math.max(0, Math.round(Number(bid.providerUnitMicros || raw)));
  const fee = route === "INTERNAL"
    ? 0
    : Math.max(0, Math.round(Number(bid.genrlFeeMicros ?? Math.round(prov * 0.08))));
  const buyer = route === "INTERNAL"
    ? 0
    : Math.max(0, Math.round(Number(bid.buyerUnitMicros ?? (prov + fee))));
  const feeBps = route === "INTERNAL" || prov <= 0 ? 0 : Math.round((fee / prov) * 10_000);
  const sourceSlug = String(bid.sourceSlug || bid.source || "").trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_");
  const providerSlug = String(bid.providerSlug || sourceSlug || bid.source || "").trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_");
  const bidID = String(bid.optionID || "").trim() || `bid_${sourceSlug || "source"}_${i}`;
  const validUntil = new Date(Date.now() + (4 * 60 * 1000)).toISOString();
  return {
    bid_id: bidID,
    provider_source_id: sourceSlug || providerSlug || `source_${i}`,
    provider_owner: route === "INTERNAL" ? "you" : bid.source,
    spoke_id: String(bid.spokeID || ""),
    provider_name: bid.source,
    region: bid.location,
    hardware_type: bid.gpu,
    gpu_count: 1,
    stock: bid.capacity,
    service_type: "COMPUTE",
    billing_unit: "GPU_HOUR",
    raw_unit_cost_micros: String(raw),
    provider_unit_price_micros: String(prov),
    genrl_fee_bps: Math.max(0, feeBps),
    genrl_unit_fee_micros: String(fee),
    buyer_unit_price_micros: String(buyer),
    route_type: route,
    valid_until: validUntil,
    quote_hash: `qh_${(sourceSlug || providerSlug || i.toString()).replace(/[^a-z0-9_]+/gi, "")}_${i}`,
    reliability: 0.9,
  };
}

/** Build a quote from a financial bid and an estimated quantity (in unit). */
export function buildQuote(bid: FinancialBid, estQty: number): QuoteResponse {
  const qty = Math.max(estQty, 0);
  const buyerUnit = Number(bid.buyer_unit_price_micros);
  const feeUnit = Number(bid.genrl_unit_fee_micros);
  const debit = Math.round(qty * buyerUnit);
  const reserve = Math.round(debit * 1.15);
  const validUntil = new Date(Date.now() + (4 * 60 * 1000)).toISOString();
  return {
    quote_id: `q_${bid.bid_id}_${Math.round(qty * 100)}`,
    bid_id: bid.bid_id,
    route_type: bid.route_type,
    billing_unit: bid.billing_unit,
    estimated_quantity: String(qty),
    provider_unit_price_micros: bid.provider_unit_price_micros,
    genrl_fee_bps: bid.genrl_fee_bps,
    genrl_unit_fee_micros: String(feeUnit),
    buyer_unit_price_micros: bid.buyer_unit_price_micros,
    estimated_debit_micros: String(debit),
    reserve_amount_micros: String(reserve),
    valid_until: validUntil,
    quote_hash: bid.quote_hash,
  };
}

export const ROUTE_LABEL: Record<RouteType, string> = {
  INTERNAL: "Internal",
  PEER: "Peer",
  GENRL_NATIVE: "GENRL Native",
  HYBRID: "Hybrid",
};

export const SETTLEMENT_LABEL: Record<SettlementStatus, string> = {
  PENDING: "Pending",
  METERED: "Metered",
  RECONCILED: "Reconciled",
  FINALIZED: "Finalized",
  PAID: "Paid",
  DISPUTED: "Disputed",
  FAILED: "Failed",
};
